import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import offerPageURL from 'lib/offer/offerPageURL'
import { getSuggestedDates } from 'selectors/offerSelectors'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import config from 'constants/config'
import { buildSearchParamsFromFilters, buildSuggestedDatesParamsKey, convertFlexibleDatesToTravelDates, encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { SOURCE_TYPES } from 'constants/source'
import { queryKeySource, urlHashDatePicker, queryKeyAdults, queryKeyChildren, queryKeyDuration } from 'constants/url'
import useOfferMetaData from './useOfferMetaData'
import { OfferUnavailableReason } from 'lib/search/constants'
import { isLEHotel } from 'lib/offer/offerTypes'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'

interface Options {
  filters?: App.OfferListFilters
  bundledOfferId?: string
  offerLinkIncludesFilters?: boolean
  searchId?: string
  showDatePicker?: boolean
  utmBp?: string
  ruleId?: string
}

function useOfferUrl(offer: App.AnyOffer, options: Options): string
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined
function useOfferUrl(offer: App.AnyOffer | undefined, options: Options): string | undefined {
  const offerId = offer?.id ?? ''
  const { filters, bundledOfferId, offerLinkIncludesFilters, showDatePicker } = options
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector(state => getSuggestedDates(state, flexibleSearchFilterKey, offerId))
  const tripId = useAppSelector(state => getImmersiveTripId(state))
  const searchMetadata = useOfferMetaData(offerId, options.filters)
  const maldivesBundleContext = useMaldivesDirectBundleContext()
  const departureAirportCodeForUrl = maldivesBundleContext.departureAirportCode ?? filters?.departureAirportCode
  const filtersForUrl = useMemo<App.OfferListFilters | undefined>(() => {
    if (!departureAirportCodeForUrl) return filters
    if (filters?.departureAirportCode === departureAirportCodeForUrl) return filters
    return {
      ...(filters ?? {}),
      departureAirportCode: departureAirportCodeForUrl,
    }
  }, [filters, departureAirportCodeForUrl])

  const isPropertySearchTile = useMemo(() => {
    return (isLEHotel(offer) && filters?.propertyId && !filters?.searchNearby && offer.property?.id === filters.propertyId)
  }, [filters, offer])

  const overrideOccupancy: Array<App.SearchOccupancy> = useMemo(() => searchMetadata?.rooms.map(room => room.occupancy) ?? [], [searchMetadata])

  return useMemo(() => {
    if (!offer) return undefined

    let offerUrlSuggestedDates: App.OfferSuggestedDates | undefined = suggestedDates
    if (!suggestedDates?.checkIn && !suggestedDates?.checkOut && filters?.flexibleNights && filters?.flexibleMonths) {
      const { checkIn, checkOut } = convertFlexibleDatesToTravelDates(filters.flexibleNights as FLEXIBLE_DURATION_RANGE, filters.flexibleMonths)
      offerUrlSuggestedDates = {
        ...suggestedDates,
        checkIn,
        checkOut,
      }
    }

    const isCharterBundle = filtersForUrl?.charterBundle
    const charterBundleHasNoDates = isCharterBundle && (!filtersForUrl?.checkIn || !filtersForUrl?.checkOut)
    const noDatesInUrl =
      charterBundleHasNoDates ||
      ((!!isPropertySearchTile && searchMetadata?.unavailableReason === OfferUnavailableReason.NUM_NIGHTS_UNSUPPORTED) || showDatePicker)

    const searchParams = offerLinkIncludesFilters ? buildSearchParamsFromFilters(filtersForUrl, offerUrlSuggestedDates, { noDatesInUrl }) : new URLSearchParams()

    if (maldivesBundleContext.charterBundle && maldivesBundleContext.duration) {
      searchParams.set(queryKeyDuration, String(maldivesBundleContext.duration))
    }

    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) {
      // Make sure immersive trip header persists when opening offer links in new tab
      searchParams.set('tripId', tripId)
    }
    if (bundledOfferId) {
      searchParams.set('bundledOfferId', bundledOfferId)
    }

    const departureMonths = filtersForUrl?.departureMonths?.filter(Boolean) ?? []
    if (departureMonths.length > 0) {
      departureMonths.forEach(month => {
        searchParams.append('departureMonths', month)
      })
    }

    if (offer.type === OFFER_TYPE_BED_BANK && filtersForUrl?.source === SOURCE_TYPES.METASEARCH) {
      searchParams.set(queryKeySource, filtersForUrl.source)
    }

    if (options.searchId) {
      searchParams.set('searchId', options.searchId)
    }

    if (options.utmBp) {
      searchParams.set('utm_bp', options.utmBp)
      if (options.utmBp === '3') {
        searchParams.set('gha_price', '1')
      }
    }

    if (options.ruleId) {
      searchParams.set('ruleId', options.ruleId)
    }

    // Skip occupancy override for charter bundles - preserve user's selected occupancy
    // Use metadata occupancy whenever we have metadata rooms (they represent what the search actually returned)
    const shouldOverrideOccupancy = !isCharterBundle && overrideOccupancy.length > 0

    if (shouldOverrideOccupancy) {
      const adults = overrideOccupancy.map(room => room.numberOfAdults).join(',')

      const children = overrideOccupancy.map(room => encodeChildrenOccupancy(room.agesOfChildren)).join(',')

      // Set the occupancy params
      searchParams.set(queryKeyAdults, adults)
      searchParams.set(queryKeyChildren, children)
    }

    return offerPageURL(offer, searchParams, showDatePicker ? urlHashDatePicker : undefined)
  }, [
    suggestedDates,
    filters,
    filtersForUrl,
    isPropertySearchTile,
    searchMetadata?.unavailableReason,
    offerLinkIncludesFilters,
    tripId,
    bundledOfferId,
    offer,
    options.searchId,
    showDatePicker,
    options.utmBp,
    options.ruleId,
    overrideOccupancy,
    maldivesBundleContext.duration,
    maldivesBundleContext.charterBundle,
  ])
}

export default useOfferUrl
